// src/components/EditStagesModal/EditStagesModal.jsx
import React, { useCallback, useEffect, useState, useRef } from "react";
import axiosInstace from "../../axiosInstace";
import Toast from "../../components/Toast/Toast";
import AddButton from "../../components/AddButton";
import DeleteButton from "../../components/DeleteButton/DeleteButton";
import CheckList from "../../components/CheckList/CheckList";
import TooltipPortal from "../../components/TooltipPortal/TooltipPortal";
import "./EditStagesModal.scss";
import DeleteWarningModal from "../../components/DeleteWarningModal/DeleteWarningModal";

const FLAG_OPTIONS = [
  "YTS",
  "PROGRESS",
  "WAITING",
  "ON_HOLD",
  "COMPLETED",
  "ABORTED",
];
const FLAG_SET = new Set(FLAG_OPTIONS);

export default function EditStagesModal({
  projectId,
  onClose,
  onSuccess,
  onShowToast,
  parentSubmit,
  scheduleProjectRefreshDebounced,
  canEdit = true,
}) {
  // ------- Animation / mount state -------
  const ANIM_MS = 260;
  const [mounted, setMounted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const exitTimerRef = useRef(null);
  // confirmation for marking final
  const [confirmFinal, setConfirmFinal] = useState({
    open: false,
    stageId: null,
    stageName: "",
    nextChecked: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    stageId: null,
    stageName: "",
  });

  // states
  const [saving, setSaving] = useState(false);
  const [stages, setStages] = useState([]);
  const [allStages, setAllStages] = useState([]);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "success",
  });

  // local changed markers and pill animation
  const [dirtyStageIds, setDirtyStageIds] = useState(new Set());
  const [animatingPill, setAnimatingPill] = useState(null);
  const pillTimerRef = useRef(null);

  // notify wrapper
  const notify = useCallback(
    (message, type = "success") => {
      if (typeof onShowToast === "function") {
        try {
          onShowToast(message, type);
        } catch (e) {
          setToast({ open: true, message, type });
        }
      } else {
        setToast({ open: true, message, type });
      }
    },
    [onShowToast],
  );
  const closeLocalToast = () => setToast((t) => ({ ...t, open: false }));

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 12);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    return () => {
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      if (pillTimerRef.current) {
        clearTimeout(pillTimerRef.current);
        pillTimerRef.current = null;
      }
    };
  }, []);

  const handleClose = useCallback(
    (skipAnimation = false) => {
      if (!mounted) return;
      if (exitTimerRef.current) {
        clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      if (skipAnimation) {
        setIsVisible(false);
        setMounted(false);
        try {
          onClose?.();
        } catch (e) {
          console.debug("onClose threw:", e);
        }
        return;
      }
      setIsVisible(false);
      exitTimerRef.current = setTimeout(() => {
        exitTimerRef.current = null;
        setMounted(false);
        try {
          onClose?.();
        } catch (e) {
          console.debug("onClose threw:", e);
        }
      }, ANIM_MS + 10);
    },
    [mounted, onClose],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Esc") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);
  // called from the Final checkbox onChange
  const attemptToggleFinal = (id, checked, stageName) => {
    if (!checked) {
      toggleFinal(id, false);
      markDirty(id);
      return;
    }

    const initialIndex = getInitialIndex(stages);
    const finalIndex = getStageIndexById(stages, id);

    if (initialIndex !== -1 && finalIndex <= initialIndex) {
      notify("Final stage must be below the Initial stage.", "warning");
      return;
    }

    // existing confirmation logic
    if (!isServerId(id)) {
      toggleFinal(id, true);
      markDirty(id);
      return;
    }

    setConfirmFinal({
      open: true,
      stageId: id,
      stageName: stageName || "",
      nextChecked: true,
    });
  };

  const confirmFinalProceed = () => {
    const { stageId, nextChecked } = confirmFinal;
    // apply the change
    toggleFinal(stageId, nextChecked);
    markDirty(stageId);
    notify(
      `Selecting "${
        confirmFinal.stageName || ""
      }" as FINAL will move all tasks in that stage to the project's final stage. Click Save to persist.`,
      "info",
    );
    // close dialog
    setConfirmFinal({
      open: false,
      stageId: null,
      stageName: "",
      nextChecked: true,
    });
  };

  const confirmFinalCancel = () => {
    // just close the dialog, do not change the stage's final value
    setConfirmFinal({
      open: false,
      stageId: null,
      stageName: "",
      nextChecked: true,
    });
  };

  // ---- helpers ----
  const displayFlagLabel = (flag) =>
    String(flag ?? "")
      .toLowerCase()
      .replace(/_/g, " ")
      .trim();

  const animatePill = (stageId, flag) => {
    if (pillTimerRef.current) {
      clearTimeout(pillTimerRef.current);
      pillTimerRef.current = null;
    }
    setAnimatingPill({ stageId, flag });
    pillTimerRef.current = setTimeout(() => {
      setAnimatingPill(null);
      pillTimerRef.current = null;
    }, 500);
  };
  const getInitialIndex = (list) => list.findIndex((s) => s.initial);

  const getStageIndexById = (list, id) =>
    list.findIndex((s) => s.stageId === id);

  const normalizeFlag = useCallback((raw) => {
    if (raw === null || raw === undefined) return "YTS";
    const v = String(raw).trim().toLowerCase();
    if (!v) return "YTS";
    if (v === "yts" || v.includes("yts")) return "YTS";
    if (
      v.includes("progress") ||
      v.includes("in-progress") ||
      v.includes("in progress") ||
      /\d+%/.test(v)
    )
      return "PROGRESS";
    if (v.includes("waiting")) return "WAITING";
    if (v.includes("on hold") || v.includes("on_hold") || v === "on_hold")
      return "ON_HOLD";
    if (v.includes("completed") || v.includes("done") || v.includes("finish"))
      return "COMPLETED";
    if (v.includes("abort") || v.includes("aborted")) return "ABORTED";
    const upper = v.toUpperCase();
    if (FLAG_SET.has(upper)) return upper;
    return String(raw).toUpperCase();
  }, []);

  // PICK position entry AND CAPTURE stagePositionId
  const pickPositionEntry = (stage) => {
    const arr =
      stage.stagePositions ||
      stage.stagePositionResponseDTOList ||
      stage.stagePositionRequestDTOList ||
      [];
    if (!arr || arr.length === 0)
      return {
        position: 0,
        initial: false,
        finalPosition: false,
        stagePositionId: null,
      };

    const defaultId =
      stage.defaultStagePositionId ?? stage.stagePositionId ?? 0;
    let chosen = null;
    if (defaultId) {
      chosen = arr.find(
        (p) =>
          p.stagePositionId === defaultId ||
          p.position === defaultId ||
          p.id === defaultId,
      );
    }
    if (!chosen) chosen = arr[0];

    return {
      position: Number(chosen.position ?? 0),
      initial: !!chosen.initial,
      finalPosition: !!(chosen.finalPosition ?? chosen.final),
      stagePositionId: chosen.stagePositionId ?? chosen.id ?? null,
    };
  };

  // load initial data and PRESERVE stagePositionId
  useEffect(() => {
    let mountedLocal = true;
    const fetchData = async () => {
      try {
        const [projRes, allRes] = await Promise.all([
          axiosInstace.get(`/api/emp/project/${projectId}/stages`),
          axiosInstace.get(`/api/emp/stages`),
        ]);

        const projRaw = projRes?.data?.data ?? projRes?.data ?? [];

        const looksLikeStage = (o) =>
          o &&
          typeof o === "object" &&
          (o.stageId !== undefined ||
            o.stageName !== undefined ||
            o.name !== undefined);

        const flattenedStages = [];
        if (Array.isArray(projRaw)) {
          projRaw.forEach((item) => {
            const candidates = [
              item.stageResponseDTOList,
              item.stageResponseList,
              item.stageResponseDTOs,
              item.stageResponse,
              item.stageDTOList,
              item.stages,
              item.stageList,
            ];
            let pushed = false;
            for (const c of candidates) {
              if (Array.isArray(c) && c.length > 0) {
                flattenedStages.push(...c);
                pushed = true;
                break;
              }
            }
            if (!pushed) {
              if (looksLikeStage(item)) flattenedStages.push(item);
              else {
                for (const val of Object.values(item)) {
                  if (looksLikeStage(val)) {
                    flattenedStages.push(val);
                    pushed = true;
                    break;
                  }
                  if (
                    Array.isArray(val) &&
                    val.length &&
                    looksLikeStage(val[0])
                  ) {
                    flattenedStages.push(...val);
                    pushed = true;
                    break;
                  }
                }
              }
            }
          });
        }
        if (flattenedStages.length === 0 && Array.isArray(projRaw)) {
          const fallback = projRaw.filter((x) => looksLikeStage(x));
          if (fallback.length) flattenedStages.push(...fallback);
        }

        const normalizedProject = (
          Array.isArray(flattenedStages) ? flattenedStages : []
        ).map((s, idx) => {
          const stageObj = s || {};
          const posEntry = pickPositionEntry(stageObj);
          const stageId =
            stageObj.stageId ?? stageObj.id ?? `proj-${idx}-${Date.now()}`;
          const rawFlag = stageObj.stageFlag ?? stageObj.flag ?? "";
          const stageName = stageObj.stageName ?? stageObj.name ?? "";

          return {
            stageId,
            stageName,
            stageFlag: normalizeFlag(rawFlag),
            _originalFlag: rawFlag ?? "",
            position: posEntry.position ?? idx + 1,
            initial: !!posEntry.initial,
            final: !!posEntry.finalPosition,
            stagePositionId: posEntry.stagePositionId ?? null, // PRESERVE ID here
            _imported: false,
          };
        });

        const allRaw = allRes?.data?.data ?? allRes?.data ?? [];
        const normalizedAll = (Array.isArray(allRaw) ? allRaw : []).map(
          (a, idx) => {
            const posEntry = pickPositionEntry(a);
            const stageId =
              a.stageId ??
              a.id ??
              (a.stage && a.stage.stageId) ??
              `all-${idx}-${Date.now()}`;
            const rawFlag =
              a.stageFlag ?? a.flag ?? (a.stage && a.stage.stageFlag) ?? "";
            return {
              stageId,
              stageName:
                a.stageName ?? a.name ?? (a.stage && a.stage.stageName) ?? "",
              stageFlag: rawFlag ?? "",
              _normalizedFlag: normalizeFlag(rawFlag),
              position: posEntry.position ?? idx + 1,
              initial: !!posEntry.initial,
              final: !!posEntry.finalPosition,
              stagePositionId: posEntry.stagePositionId ?? null,
            };
          },
        );

        if (!mountedLocal) return;
        setStages(normalizedProject);
        setAllStages(normalizedAll);
      } catch (err) {
        console.error("EditStagesModal: fetch failed", err);
        notify("Failed to fetch stages", "error");
      }
    };

    fetchData();
    return () => {
      mountedLocal = false;
    };
  }, [projectId, normalizeFlag, notify]);

  // core local edit helpers
  const isServerId = (id) =>
    id !== undefined &&
    id !== null &&
    !String(id).startsWith("new-") &&
    !String(id).startsWith("imp-");
  const handleEditStage = (id, field, value) =>
    setStages((prev) =>
      prev.map((s) => (s.stageId === id ? { ...s, [field]: value } : s)),
    );
  const handleAddStageLocal = () =>
    setStages((prev) => {
      const next = [
        ...prev,
        {
          stageId: `new-${Date.now()}`,
          stageName: "New Stage",
          stageFlag: "YTS",
          position: prev.length + 1,
          initial: prev.length === 0,
          final: false,
          _imported: true,
        },
      ];
      return next.map((s, idx) => ({ ...s, position: idx + 1 }));
    });
  const handleDeleteStage = (id) =>
    setStages((prev) =>
      prev
        .filter((s) => s.stageId !== id)
        .map((s, idx) => ({ ...s, position: idx + 1 })),
    );
  const toggleInitial = (id, checked) =>
    setStages((prev) => {
      if (!checked) return prev.map((s) => ({ ...s, initial: false }));

      const newInitialIndex = prev.findIndex((s) => s.stageId === id);
      const finalIndex = prev.findIndex((s) => s.final);

      if (finalIndex !== -1 && newInitialIndex >= finalIndex) {
        notify("Initial stage must be above the Final stage.", "warning");
        return prev;
      }

      return prev.map((s) => ({
        ...s,
        initial: s.stageId === id,
      }));
    });

  const toggleFinal = (id, checked) =>
    setStages((prev) =>
      prev.map((s) => ({ ...s, final: checked ? s.stageId === id : false })),
    );

  const markDirty = (id) =>
    setDirtyStageIds((prev) => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });

  // normalize payload for parent - PRESERVE oldStagePositionId if present
  const normalizeForParent = (payload) => ({
    stageRequestDTOS: (payload?.stageRequestDTOS || []).map((s) => ({
      ...(s.stageId != null ? { stageId: s.stageId } : {}),
      stageName: s.stageName,
      stageFlag:
        s.stageFlag == null
          ? null
          : String(s.stageFlag).trim().toUpperCase().replace(/\s+/g, "_"),
      stagePositionRequestDTOList: (s.stagePositionRequestDTOList || []).map(
        (p) => {
          const oldStagePositionId =
            p.oldStagePositionId ?? p.stagePositionId ?? p.id ?? null;
          return {
            ...(oldStagePositionId != null ? { oldStagePositionId } : {}),
            position: Number(p.position ?? p.pos ?? 0),
            initial: Boolean(p.initial ?? false),
            finalPosition: Boolean(p.finalPosition ?? p.final ?? false),
          };
        },
      ),
    })),
  });

  const handleSave = async () => {
    const initialIndex = stages.findIndex((s) => s.initial);
    const finalIndex = stages.findIndex((s) => s.final);

    if (
      initialIndex !== -1 &&
      finalIndex !== -1 &&
      initialIndex >= finalIndex
    ) {
      notify(
        "Initial stage must be positioned above the Final stage before saving.",
        "error",
      );
      return;
    }

    if (!canEdit) {
      notify("You don't have permission to edit stages", "info");
      return;
    }

    try {
      setSaving(true);

      // ensure ordering
      const ordered = stages.map((s, idx) => ({ ...s, position: idx + 1 }));

      // build stageRequestDTOS with oldStagePositionId where present
      const stageRequestDTOS = ordered.map((s) => {
        const base = {
          stageName: s.stageName,
          stageFlag: s.stageFlag,
          stagePositionRequestDTOList: [
            {
              ...(s.stagePositionId != null
                ? { oldStagePositionId: s.stagePositionId }
                : {}),
              position: Number(s.position ?? ordered.indexOf(s) + 1),
              initial: !!s.initial,
              finalPosition: !!s.final,
            },
          ],
        };
        return isServerId(s.stageId) ? { stageId: s.stageId, ...base } : base;
      });

      const payload = { stageRequestDTOS };
      console.log(
        "[EditStagesModal] payload to send:",
        JSON.stringify(payload, null, 2),
      );

      // POST the payload (parentSubmit or direct)
      let postRes = null;
      if (typeof parentSubmit === "function") {
        const normalizedToParent = normalizeForParent(payload);
        console.log(
          "[EditStagesModal] calling parentSubmit with normalized:",
          JSON.stringify(normalizedToParent, null, 2),
        );
        postRes = await parentSubmit(normalizedToParent);
      } else {
        console.log(
          "[EditStagesModal] posting payload directly:",
          JSON.stringify(payload, null, 2),
        );
        const axiosRes = await axiosInstace.post(
          `/api/emp/stages/${encodeURIComponent(projectId)}/add-stages`,
          payload,
          { headers: { "Content-Type": "application/json" } },
        );
        postRes = axiosRes?.data ?? axiosRes;
      }

      notify("Stages saved successfully", "success");

      // Fetch both /full and /stages in parallel
      const fullUrl = `/api/emp/project/${projectId}/full`;
      const stagesUrl = `/api/emp/project/${projectId}/stages`;
      console.log(
        "[EditStagesModal] fetching updated endpoints:",
        fullUrl,
        stagesUrl,
      );

      const [fullResp, stagesResp] = await Promise.all([
        axiosInstace.get(fullUrl).catch((e) => {
          console.warn("[EditStagesModal] /full fetch failed:", e && e.message);
          return null;
        }),
        axiosInstace.get(stagesUrl).catch((e) => {
          console.warn(
            "[EditStagesModal] /stages fetch failed:",
            e && e.message,
          );
          return null;
        }),
      ]);

      const fullPayload = fullResp
        ? (fullResp.data?.data ?? fullResp.data ?? fullResp)
        : null;
      const stagesPayload = stagesResp
        ? (stagesResp.data?.data ?? stagesResp.data ?? stagesResp)
        : null;

      console.log("[EditStagesModal] /full payload:", fullPayload);
      console.log("[EditStagesModal] /stages payload:", stagesPayload);

      // Normalize stagesPayload into freshStages (same flatten logic)
      let freshStages = null;
      if (Array.isArray(stagesPayload)) {
        const flattened = [];
        stagesPayload.forEach((item) => {
          const candidates = [
            item.stageResponseDTOList,
            item.stageResponseList,
            item.stageResponseDTOs,
            item.stageResponse,
            item.stageDTOList,
            item.stages,
            item.stageList,
          ];
          let pushed = false;
          for (const c of candidates) {
            if (Array.isArray(c) && c.length > 0) {
              flattened.push(...c);
              pushed = true;
              break;
            }
          }
          if (!pushed) {
            if (
              item &&
              typeof item === "object" &&
              (item.stageId || item.stageName)
            )
              flattened.push(item);
          }
        });

        if (flattened.length > 0) {
          freshStages = flattened.map((stageObj, idx) => {
            const posEntry = pickPositionEntry(stageObj);
            const stageId =
              stageObj.stageId ?? stageObj.id ?? `srv-${idx}-${Date.now()}`;
            const rawFlag = stageObj.stageFlag ?? stageObj.flag ?? "";
            const stageName = stageObj.stageName ?? stageObj.name ?? "";
            return {
              stageId,
              stageName,
              stageFlag: normalizeFlag(rawFlag),
              _originalFlag: rawFlag ?? "",
              position: posEntry.position ?? idx + 1,
              initial: !!posEntry.initial,
              final: !!posEntry.finalPosition,
              stagePositionId: posEntry.stagePositionId ?? null,
              _imported: false,
            };
          });
        }
      }

      // If freshStages exists and has entries, apply it. Otherwise keep `ordered` (fallback) — do NOT clear.
      if (Array.isArray(freshStages) && freshStages.length > 0) {
        setStages(freshStages);
        console.log(
          "[EditStagesModal] setStages -> freshStages (count):",
          freshStages.length,
        );
      } else {
        // fallback: do not wipe UI — reuse the ordered data we just sent
        setStages(ordered);
        console.log(
          "[EditStagesModal] freshStages invalid/empty — preserving ordered local state.",
        );
      }

      // Optionally call onSuccess with both responses so parent can update
      if (typeof onSuccess === "function") {
        try {
          await onSuccess({
            postRes,
            full: fullPayload,
            stages: stagesPayload,
          });
        } catch (e) {
          console.debug("onSuccess threw:", e);
        }
      }

      // reset dirty flags
      setDirtyStageIds(new Set());

      if (typeof scheduleProjectRefreshDebounced === "function") {
        try {
          scheduleProjectRefreshDebounced(1200);
        } catch (e) {}
      }

      handleClose();
      return postRes;
    } catch (err) {
      console.error("EditStagesModal: save failed", err);
      notify(
        err?.response?.data?.message || err?.message || "Failed to save stages",
        "error",
      );
      return null;
    } finally {
      setSaving(false);
    }
  };
  const confirmDeleteStage = () => {
    const { stageId } = deleteConfirm;

    handleDeleteStage(stageId);

    setDirtyStageIds((prev) => {
      const n = new Set(prev);
      n.delete(stageId);
      return n;
    });

    setDeleteConfirm({
      open: false,
      stageId: null,
      stageName: "",
    });
  };

  const cancelDeleteStage = () => {
    setDeleteConfirm({
      open: false,
      stageId: null,
      stageName: "",
    });
  };

  const handleAddFromAvailable = (avail) => {
    if (!canEdit) {
      notify("You don't have permission to add stages", "info");
      return;
    }
    const nameKey = (avail.stageName ?? "").trim().toLowerCase();
    if (
      stages.some((s) => (s.stageName ?? "").trim().toLowerCase() === nameKey)
    ) {
      notify(`Stage "${avail.stageName}" already exists in project.`, "info");
      return;
    }
    const newStageLocal = {
      stageId: `imp-${avail.stageId}-${Date.now()}`,
      stageName: avail.stageName ?? "",
      stageFlag:
        avail._normalizedFlag ??
        normalizeFlag(avail.stageFlag ?? avail.flag ?? ""),
      position: stages.length + 1,

      // 🚫 DO NOT import checklist state
      initial: false,
      final: false,

      _imported: true,
      _originalFlag: avail.stageFlag ?? avail.flag ?? "",
    };

    setStages((prev) =>
      [...prev, newStageLocal].map((s, idx) => ({ ...s, position: idx + 1 })),
    );
    setDirtyStageIds((prev) => new Set(prev).add(newStageLocal.stageId));
    notify(
      `Added "${avail.stageName}" locally — click Save to persist.`,
      "success",
    );
  };

  if (!mounted) return null;

  return (
    <div
      className={`editstagemodal-overlay ${isVisible ? "visible" : "hidden"}`}
      role="presentation"
    >
      {toast.open && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeLocalToast}
        />
      )}

      <div
        className={`editstagemodal-modal-center ${
          isVisible ? "pop-in" : "pop-out"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Edit stages"
      >
        <div className="editstagemodal-modal-header">
          <div className="editstagemodal-header-title">
            Status Management
            <TooltipPortal label="The order you set here will be reflected across all views, especially Kanban.">
              <span className="help-icon">!</span>
            </TooltipPortal>
          </div>

          {/* <button className="editstagemodal-close-btn" onClick={() => handleClose()} aria-label="Close stages modal">
            ✖
          </button> */}
        </div>

        <div className="editstagemodal-modal-body">
          <div className="editstagemodal-middle-panel">
            <h3 className="sr-only">Project Stages</h3>
            <div className="editstagemodal-stages-table-wrap">
              <table className="editstagemodal-stages-table" role="table">
                <thead>
                  <tr>
                    <th style={{ minWidth: 220 }}>Name</th>
                    <th style={{ width: 260 }}>Flag</th>
                    <th style={{ width: 90 }}>Initial</th>
                    <th style={{ width: 90 }}>Final</th>
                    <th style={{ width: 64 }} />
                  </tr>
                </thead>
                <tbody>
                  {stages.map((s) => (
                    <tr
                      key={s.stageId}
                      className={
                        dirtyStageIds.has(s.stageId) ? "stage-dirty" : ""
                      }
                    >
                      <td data-label="Name">
                        <input
                          className="editstagemodal-text-input"
                          value={s.stageName}
                          onChange={(e) => {
                            handleEditStage(
                              s.stageId,
                              "stageName",
                              e.target.value,
                            );
                            markDirty(s.stageId);
                          }}
                          disabled={!canEdit}
                        />
                      </td>
                      <td data-label="Flag">
                        <div
                          className="editstagemodal-flag-pills two-rows"
                          role="group"
                          aria-label={`Flags for ${s.stageName}`}
                        >
                          {FLAG_OPTIONS.map((flag) => {
                            const active = s.stageFlag === flag;
                            const display = displayFlagLabel(flag);
                            const isAnimating =
                              animatingPill &&
                              animatingPill.stageId === s.stageId &&
                              animatingPill.flag === flag;
                            const handlePillClickLocal = () => {
                              if (!canEdit) return;
                              handleEditStage(s.stageId, "stageFlag", flag);
                              markDirty(s.stageId);
                              animatePill(s.stageId, flag);
                              notify(
                                `Flag changed to "${display}" click Save.`,
                                "info",
                              );
                            };
                            return (
                              <button
                                key={flag}
                                type="button"
                                className={`editstagemodal-flag-pill ${
                                  active ? "active" : ""
                                } ${isAnimating ? "clicked" : ""} plain-flag`}
                                onClick={handlePillClickLocal}
                                disabled={!canEdit}
                                aria-pressed={active}
                                title={display}
                              >
                                {display}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                      <td data-label="Initial">
                        <CheckList
                          id={`initial-${s.stageId}`}
                          label=""
                          checked={!!s.initial}
                          onChange={(e) => {
                            toggleInitial(s.stageId, e.target.checked);
                            markDirty(s.stageId);
                          }}
                        />
                      </td>
                      <td data-label="Final">
                        <CheckList
                          id={`final-${s.stageId}`}
                          label=""
                          checked={!!s.final}
                          onChange={(e) =>
                            attemptToggleFinal(
                              s.stageId,
                              e.target.checked,
                              s.stageName,
                            )
                          }
                        />
                      </td>
                      <td data-label="Actions">
                        <TooltipPortal label={`Delete ${s.stageName}`}>
                          <DeleteButton
                            onClick={() =>
                              setDeleteConfirm({
                                open: true,
                                stageId: s.stageId,
                                stageName: s.stageName,
                              })
                            }
                            title="Delete Stage"
                          />
                        </TooltipPortal>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="editstagemodal-add-below">
                <AddButton
                  size={32}
                  onClick={handleAddStageLocal}
                  tooltip="Add Stage (local only)"
                />
                <div style={{ marginLeft: 12, fontSize: 12, color: "#666" }}>
                  <span style={{ fontWeight: 600 }}>{dirtyStageIds.size}</span>{" "}
                  unsaved change(s)
                </div>
              </div>

              {stages.length === 0 && (
                <div className="editstagemodal-empty">No stages defined.</div>
              )}
            </div>
          </div>

          <div className="editstagemodal-right-panel">
            <h3 className="sr-only">Available Stages</h3>
            <div className="editstagemodal-available-list">
              <table className="editstagemodal-available-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Flag</th>
                    <th style={{ width: 110 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {allStages.map((a) => {
                    const added = stages.some(
                      (s) =>
                        s._imported === true &&
                        (s.stageName ?? "").trim().toLowerCase() ===
                          (a.stageName ?? "").trim().toLowerCase(),
                    );
                    return (
                      <tr key={a.stageId}>
                        <td>{a.stageName}</td>
                        <td>
                          <div className="editstagemodal-available-single-flag">
                            <span className="single-flag-pill plain-flag-pill">
                              {(
                                a._normalizedFlag ??
                                normalizeFlag(a.stageFlag ?? "")
                              ).toLowerCase()}
                            </span>
                          </div>
                        </td>
                        <td>
                          <button
                            className={`editstagemodal-available-add-btn ${
                              added ? "added" : ""
                            }`}
                            onClick={() => handleAddFromAvailable(a)}
                            disabled={!canEdit || added}
                          >
                            {added ? "Added" : "Add"}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {allStages.length === 0 && (
                <div className="editstagemodal-empty">No available stages.</div>
              )}
            </div>
          </div>
        </div>

        <div className="editstagemodal-modal-footer">
          <button
            className="editstagemodal-btn editstagemodal-btn-ghost"
            onClick={() => handleClose()}
          >
            Cancel
          </button>
          <button
            className="editstagemodal-btn editstagemodal-btn-primary"
            onClick={handleSave}
            disabled={saving || !canEdit}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
      {/* Confirm Final change dialog */}
      {confirmFinal.open && (
        <div
          className="editstagemodal-confirm-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="editstagemodal-confirm-card">
            <div className="confirm-title">Confirm setting final stage</div>
            <div className="confirm-body">
              Selecting "<strong>{confirmFinal.stageName}</strong>" as the{" "}
              <strong>FINAL</strong> stage will move{" "}
              <em>all tasks currently in this stage</em> to the project's final
              stage.
              <div style={{ marginTop: 8, fontSize: 13, color: "#666" }}>
                Do you want to proceed?
              </div>
            </div>
            <div className="confirm-actions" style={{ marginTop: 12 }}>
              <button
                className="editstagemodal-btn editstagemodal-btn-ghost"
                onClick={confirmFinalCancel}
              >
                Cancel
              </button>
              <button
                className="editstagemodal-btn editstagemodal-btn-primary"
                onClick={confirmFinalProceed}
              >
                Yes, proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteConfirm.open && (
        <DeleteWarningModal
          type="warning"
          message={`Deleting "${deleteConfirm.stageName}" will move all tasks in this status to the default status.`}
          confirmText="Yes, Delete"
          cancelText="Cancel"
          onConfirm={confirmDeleteStage}
          onClose={cancelDeleteStage}
        />
      )}
    </div>
  );
}
